import { ModuleThread, Pool } from 'threads';
import { PropertyContext } from '../property-operations';
import { Point } from '../geometry/geometric-types';
import { OrderEntryDeepFragment, UnitSettingsType } from 'src/gql/graphql';

interface HashInput {
  loadedModels: string[];
  context: Omit<PropertyContext, 'loadedModels'>;
  entry: Omit<OrderEntryDeepFragment, 'orderEntryCache'>;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  };
  orderColumnIds: Array<string>;
}

interface CacheInput {
  loadedModels: Array<string>;
  context: PropertyContext;
  entry: Omit<OrderEntryDeepFragment, 'orderEntryCache'>;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  };
  orderColumnIds: Array<string>;
}

export interface BulkHashOutput {
  entryId: string;
  hash: string;
}

export type SerializeNHashModule = {
  calculateOrderEntryCacheInputsHash: (input: HashInput) => string;
  bulkComputeHashes: (
    ctx: Omit<HashInput, 'entry'>,
    entries: Array<Omit<OrderEntryDeepFragment, 'orderEntryCache'>>
  ) => Array<BulkHashOutput>;
};

export async function bulkComputeHashInWorker(
  input: Omit<CacheInput, 'entry'>,
  entries: Array<Omit<OrderEntryDeepFragment, 'orderEntryCache'>>,
  workerPool: Pool<ModuleThread<SerializeNHashModule>> | null
): Promise<Array<BulkHashOutput>> {
  if (!workerPool) {
    throw new Error('WorkerPool is not defined');
  }
  const { context, ...rest } = input;
  const { loadedModels, ...others } = context;
  const ctx = {
    ...rest,
    context: others,
    loadedModels: input.loadedModels,
  };
  try {
    return workerPool.queue(async (worker) => {
      return worker.bulkComputeHashes(ctx, entries);
    });
  } catch (e) {
    console.error('Failed to schedule work on the pool: ', e);
    throw e;
  }
}
